import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import noop from 'lodash/noop';

import {
  clearCurrentMicroSession,
  notifySignedOut,
  useApi,
  useEnvironmentContext,
} from '@sbv-clients/data-access-api';
import { useAccessToken } from '@sbv-clients/ui-shared';

import { initialState, JWTReducer } from './reducer';

type AuthContextType = {
  userId?: string;
  facilityId: string;
  facilityEmployeeId?: number;
  facilityName: string;
  impersonatorId: string;
  impersonatorUserName: string;
  isMultiFacility: boolean;
  username: string;
  authenticated?: boolean;
  useSchedulerWebsite: boolean;
  initialised?: boolean;
  isSupportUser?: boolean;
  onAuthSuccess: (token: string, refreshToken: string) => void;
  onEnterFacility: (token: string, refreshToken: string) => void;
  onSignOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthProviderProps = PropsWithChildren & {
  shouldRefreshTokenOnMount: boolean;
  type: 'portal' | 'admin';
};

const AuthProvider = ({
  children,
  shouldRefreshTokenOnMount,
  type,
}: AuthProviderProps) => {
  const refreshTokenInProgress = useRef(false);
  const queryClient = useQueryClient();
  const api = useApi();
  const { apiBaseUrl } = useEnvironmentContext();
  const {
    getAccessToken,
    getRefreshToken,
    persistAuthTokens,
    persistSessionToken,
    removeAuthTokens,
    validateAccessTokenExpiry,
    getUserInfoFromToken,
  } = useAccessToken();
  const [state, dispatch] = useReducer(JWTReducer, initialState);

  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();
  const {
    userId,
    facilityId,
    facilityEmployeeId,
    facilityName,
    impersonatorId,
    impersonatorUserName,
    username,
    isMultiFacility,
    useSchedulerWebsite,
    roles,
  } = useMemo(
    () => getUserInfoFromToken(state.accessToken),
    [getUserInfoFromToken, state.accessToken]
  );

  useEffect(() => {
    if (
      accessToken &&
      !refreshToken &&
      shouldRefreshTokenOnMount &&
      !refreshTokenInProgress.current
    ) {
      refreshTokenInProgress.current = true;
      api.accountApi
        .accountRefreshTokenByTokenPost({
          refreshTokenByJwtTokenRequest: {
            token: accessToken,
          },
        })
        .then((response) => {
          persistAuthTokens(accessToken, response.data.refreshToken as string);
        })
        .catch(noop)
        .finally(() => {
          refreshTokenInProgress.current = false;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    const initialize = async () => {
      if (accessToken) {
        dispatch({
          type: 'SIGN_IN_SUCCESS',
          payload: { accessToken },
        });
      } else {
        removeAuthTokens();
        dispatch({
          type: 'INITIALIZE',
          payload: { authenticated: false },
        });
      }
    };

    initialize();
  }, [
    accessToken,
    getAccessToken,
    removeAuthTokens,
    validateAccessTokenExpiry,
  ]);

  const onAuthSuccess = useCallback(
    (accessToken: string, refreshToken: string) => {
      queryClient.clear();
      persistAuthTokens(accessToken, refreshToken);
      persistSessionToken();
      dispatch({
        type: 'SIGN_IN_SUCCESS',
        payload: { accessToken },
      });
    },
    [persistAuthTokens, persistSessionToken, queryClient]
  );

  // Like onAuthSuccess but does NOT write a new session stamp.
  // Use this for facility switches from the header selector so the tab's
  // UserSessionId is preserved (a switch must not look like Refresh Configuration).
  const onEnterFacility = useCallback(
    (accessToken: string, refreshToken: string) => {
      queryClient.clear();
      persistAuthTokens(accessToken, refreshToken);
      dispatch({
        type: 'SIGN_IN_SUCCESS',
        payload: { accessToken },
      });
    },
    [persistAuthTokens, queryClient]
  );

  const onSignOut = useCallback(async () => {
    if (type === 'portal') {
      await Promise.all([api.authorizationApi.authLogoutPost()]).catch(noop);
    }

    // Release all employee locks before discarding the token.
    // keepalive ensures the request survives navigation/reload.
    // Failures must be swallowed — never block sign-out on this.
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        fetch(`${apiBaseUrl}/api/v2/employees/locks/mine`, {
          method: 'DELETE',
          keepalive: true,
          headers: { Authorization: `Bearer ${token}` },
        }).catch(noop);
      }
    } catch {
      // intentionally swallowed
    }

    // Clear the sessionStorage mirror of the micro-session ID
    clearCurrentMicroSession();

    removeAuthTokens();
    dispatch({ type: 'SIGN_OUT' });
    queryClient.clear();
    notifySignedOut();
    window.location.reload();
  }, [type, removeAuthTokens, queryClient, api.authorizationApi, apiBaseUrl]);

  const isSupportUser = roles.includes('Support');

  const context = useMemo(() => {
    return {
      ...state,
      userId,
      facilityId,
      facilityEmployeeId,
      facilityName,
      impersonatorId,
      impersonatorUserName,
      isMultiFacility,
      useSchedulerWebsite,
      username,
      isSupportUser,
      onAuthSuccess,
      onEnterFacility,
      onSignOut,
    };
  }, [
    facilityEmployeeId,
    facilityId,
    facilityName,
    impersonatorId,
    impersonatorUserName,
    isMultiFacility,
    isSupportUser,
    onAuthSuccess,
    onEnterFacility,
    onSignOut,
    state,
    useSchedulerWebsite,
    userId,
    username,
  ]);

  return (
    <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context)
    throw new Error('AuthContext must be placed within AuthProvider');

  return context;
};

export { AuthProvider, useAuth };
